import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Stack, Switch, Button, Tooltip
} from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import PageHeader from '../../components/common/PageHeader';
import ModuleControlPanel from '../../components/common/ModuleControlPanel';
import StatCard from '../../components/common/StatCard';
import { loadAutomation, togglePlaybook, approveAutomationExecution, rejectAutomationExecution } from './automationSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const EXEC_STATUS_COLOR = {
  completed: tokens.severity.low,
  running: tokens.severity.info,
  partially_failed: tokens.severity.medium,
  failed: tokens.severity.critical,
  dead_letter: tokens.severity.critical,
  waiting_approval: tokens.severity.medium,
  queued: tokens.severity.info,
  compensated: tokens.severity.low,
  rejected: tokens.text.muted
};

const ACTION_LABEL = {
  CREATE_TICKET: 'Create Ticket',
  NOTIFY: 'Notify',
  ISOLATE_ENDPOINT: 'Isolate Endpoint',
  BLOCK_IOC: 'Block IOC',
  TAG_ALERT: 'Tag Alert',
  WEBHOOK_CALL: 'Webhook'
};

export default function AutomationPage() {
  const dispatch = useDispatch();
  const { playbooks, executions, triggerLog } = useSelector((s) => s.automation);

  useEffect(() => { dispatch(loadAutomation()); }, [dispatch]);

  const activeCount = playbooks.filter((p) => p.isActive).length;
  const execsThisWeek = playbooks.reduce((sum, p) => sum + p.executionsThisWeek, 0);
  const failedExecs = executions.filter((e) => e.status === 'failed' || e.status === 'partially_failed').length;

  return (
    <Box>
      <PageHeader
        title="Automation & Response"
        subtitle="SOAR playbooks that react to events across every module — ticketing, notification, endpoint isolation, and IOC blocking, fully automated"
        action={<ModuleControlPanel basePath="/automation-engine" moduleLabel="Automation & Response" method="patch" />}
      />

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={3}><StatCard label="Active Playbooks" value={activeCount} icon={BoltRoundedIcon} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Total Playbooks" value={playbooks.length} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Executions This Week" value={execsThisWeek} accent={tokens.severity.info} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Failed/Partial Runs" value={failedExecs} accent={failedExecs > 0 ? tokens.severity.medium : tokens.severity.low} /></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Playbooks</Typography>
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {playbooks.map((p) => (
                <Box key={p.id} sx={{ p: 1.4, borderRadius: 1.5, border: `1px solid ${tokens.border}`, backgroundColor: tokens.bg.elevated }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.6 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: tokens.text.primary, fontWeight: 600, pr: 1 }}>{p.name}</Typography>
                    <Switch
                      size="small" checked={p.isActive}
                      onChange={(e) => dispatch(togglePlaybook({ id: p.id, isActive: e.target.checked }))}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: tokens.text.muted, fontFamily: '"JetBrains Mono", monospace' }}>
                    trigger: {p.triggerType}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.8 }}>
                    {p.steps.map((s, i) => (
                      <Chip key={`${p.id}-${s}-${i}`} label={ACTION_LABEL[s] || s} size="small" sx={{ fontSize: '0.62rem', color: tokens.text.secondary, backgroundColor: tokens.bg.surface, border: `1px solid ${tokens.border}` }} />
                    ))}
                  </Stack>
                  <Typography variant="caption" sx={{ color: tokens.text.muted, display: 'block', mt: 0.6 }}>
                    {p.executionsThisWeek} execution{p.executionsThisWeek === 1 ? '' : 's'} this week
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Execution History</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Playbook', 'Trigger Source', 'Status', 'Steps', 'Started', 'Action'].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {executions.slice(0, 16).map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.primary, maxWidth: 220 }}>{e.playbookName}</TableCell>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', color: tokens.text.secondary }}>{e.triggerSource}</TableCell>
                    <TableCell>
                      <Chip
                        label={e.status.replace('_', ' ')} size="small"
                        sx={{ color: EXEC_STATUS_COLOR[e.status], border: `1px solid ${EXEC_STATUS_COLOR[e.status]}`, backgroundColor: 'transparent' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{e.stepCount}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(e.startedAt || e.createdAt)}</TableCell>
                    <TableCell>
                      {e.status === 'waiting_approval' ? (
                        <Stack direction="row" spacing={0.5}>
                          <Button size="small" onClick={() => dispatch(approveAutomationExecution(e.id))}>Approve</Button>
                          <Button size="small" onClick={() => dispatch(rejectAutomationExecution(e.id))}>Reject</Button>
                        </Stack>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ overflow: 'hidden', mt: 2.5 }}>
        <Box sx={{ p: 2, pb: 0 }}>
          <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Trigger Log</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: tokens.text.muted, mt: 0.3 }}>
            Business events raised on this platform (incidents, vulnerabilities, ...) and whether the call into the automation engine succeeded.
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Trigger Type', 'Source', 'Matched Playbooks', 'Result', 'When'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {triggerLog.slice(0, 20).map((t) => (
              <TableRow key={t.id} hover>
                <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.primary }}>{t.triggerType}</TableCell>
                <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', color: tokens.text.secondary }}>{t.triggerSource}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem' }}>{t.matchedPlaybookCount}</TableCell>
                <TableCell>
                  {t.callSucceeded ? (
                    <Chip icon={<CheckCircleRoundedIcon sx={{ fontSize: 14 }} />} label="Succeeded" size="small" sx={{ color: tokens.severity.low, border: `1px solid ${tokens.severity.low}`, backgroundColor: 'transparent' }} />
                  ) : (
                    <Tooltip title={t.errorMessage || 'No further detail'}>
                      <Chip icon={<CancelRoundedIcon sx={{ fontSize: 14 }} />} label="Failed" size="small" sx={{ color: tokens.severity.critical, border: `1px solid ${tokens.severity.critical}`, backgroundColor: 'transparent' }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(t.createdAt)}</TableCell>
              </TableRow>
            ))}
            {!triggerLog.length && (
              <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', color: tokens.text.muted, fontSize: '0.78rem', py: 3 }}>No triggers fired yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
